import axios from "axios";
import { User } from "../../types";
import AuthService from "../AuthService";
import { ILegalService } from "../ILegalService";

const API_URL =
  process.env.NODE_ENV === "development"
    ? process.env.REACT_APP_API_URL || "http://localhost:8080"
    : process.env.REACT_APP_API_URL || "https://gdm-api.dev.rpm.platform.navify.com/gdm-patient-service/api/v1";

const IS_LOCAL_DEV = process.env.NODE_ENV === "development";
const S3_BUCKET_URL = IS_LOCAL_DEV
  ? "http://localhost:4566/legal-docs"
  : process.env.REACT_APP_S3_BUCKET_URL || "https://s3.example.com/legal-docs";

const apiClient = axios.create({
  baseURL: API_URL,
});

apiClient.interceptors.request.use(async (config) => {
  const token = await AuthService.getAccessToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

async function fetchDocumentFromS3<T>(filename: string): Promise<T> {
  const url = IS_LOCAL_DEV
    ? `${S3_BUCKET_URL}/${filename}`
    : `${S3_BUCKET_URL}/${filename}`;
  const response = await axios.get(url);
  return response.data;
}

export class LegalApiService implements ILegalService {
  async getLatestTerms() {
    try {
      const response = await apiClient.get("/legal/terms/latest");
      return response.data;
    } catch (error) {
      return fetchDocumentFromS3("terms-and-conditions.json");
    }
  }

  async getLatestPrivacy() {
    try {
      const response = await apiClient.get("/legal/privacy/latest");
      return response.data;
    } catch (error) {
      return fetchDocumentFromS3("privacy-policy.json");
    }
  }

  async acceptTerms(userId: string, version: string) {
    await apiClient.post(`/users/${userId}/acceptance/terms`, { version });
  }

  async acceptPrivacy(userId: string, version: string) {
    await apiClient.post(`/users/${userId}/acceptance/privacy`, { version });
  }

  async checkUserAcceptance(user: User) {
    const [latestTerms, latestPrivacy] = await Promise.all([
      this.getLatestTerms(),
      this.getLatestPrivacy(),
    ]);
    return {
      termsAccepted:
        user.acceptedTerms && user.termsVersion === latestTerms.version,
      privacyAccepted:
        user.acceptedPrivacy && user.privacyVersion === latestPrivacy.version,
    };
  }
}
